'use client'

import { useEffect, useRef } from 'react'
import gsap from 'gsap'
import { useIsDesktop } from '../hooks/useMediaQuery'

/**
 * CustomCursor — Hexagonal identity cursor
 * - Starts hidden (off-screen) until first mousemove
 * - Inner hex dot: snappy follow (50ms)
 * - Outer hex ring: spring trail (200ms)
 * - Hover modes: default | interactive (links/buttons) | text (paragraphs)
 * - Click: ring squishes, dot flares orange on mousedown, restores on mouseup
 * - All state in a single stable effect — no stale-closure issues
 * - Mobile: renders nothing, cursor:none not applied
 */

export default function CustomCursor() {
  const isDesktop = useIsDesktop()
  const dotRef  = useRef<HTMLDivElement>(null)
  const ringRef = useRef<HTMLDivElement>(null)
  const labelRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (!isDesktop) return
    const dot  = dotRef.current
    const ring = ringRef.current
    const label = labelRef.current
    if (!dot || !ring || !label) return

    /* ── Centre both elements on cursor using xPercent/yPercent ── */
    gsap.set([dot, ring], { xPercent: -50, yPercent: -50 })

    /* ── Direct CSS transform for zero-overhead tracking ── */
    let dotX = 0, dotY = 0
    let ringX = 0, ringY = 0
    let targetX = 0, targetY = 0
    let rafId = 0
    let tracking = false

    const lerp = (a: number, b: number, t: number) => a + (b - a) * t

    const tick = () => {
      dotX = lerp(dotX, targetX, 0.65)
      dotY = lerp(dotY, targetY, 0.65)
      ringX = lerp(ringX, targetX, 0.15)
      ringY = lerp(ringY, targetY, 0.15)
      dot.style.transform = `translate(-50%, -50%) translate3d(${dotX}px, ${dotY}px, 0)`
      ring.style.transform = `translate(-50%, -50%) translate3d(${ringX}px, ${ringY}px, 0)`
      rafId = requestAnimationFrame(tick)
    }

    /* ── GSAP quickTo kept only for mode transitions (not per-frame tracking) ── */

    /* ── State machine ── */
    type Mode = 'default' | 'interactive' | 'text'
    let mode: Mode = 'default'
    let hasEntered = false

    // `custom` = per-element data-cursor-label override (e.g. Navigation sets
    // "Home"/"Menu"/"Close"/item.num). When present it wins over the generic
    // 'Click'/'Read' copy; otherwise we fall back to the mode default.
    const enterMode = (next: Mode, custom: string | null = null) => {
      if (next === mode) {
        // Same mode, but a different labelled element under the cursor — just
        // swap the copy without re-running the (expensive) mode transition.
        if (custom && mode !== 'default' && label.textContent !== custom) {
          label.textContent = custom
        }
        return
      }
      mode = next

      if (next === 'interactive') {
        gsap.to(ring, { width: 56, height: 56, duration: 0.28, ease: 'power3.out' })
        const poly = ring.querySelector('polygon')
        if (poly) gsap.to(poly, { attr: { strokeWidth: 1.5 }, strokeOpacity: 1, duration: 0.28 })
        gsap.to(dot, { scale: 0.45, opacity: 0.5, duration: 0.2 })
        // Show label — custom override or generic "Click"
        gsap.to(label, { scale: 1, opacity: 1, duration: 0.25, ease: 'power2.out' })
        label.textContent = custom ?? 'Click'
        const svg = ring.querySelector('svg')
        if (svg) gsap.to(svg, { opacity: 1, duration: 0.2 })

      } else if (next === 'text') {
        gsap.to(ring, { width: 76, height: 76, duration: 0.32, ease: 'power2.out' })
        const svg = ring.querySelector('svg')
        if (svg) gsap.to(svg, { opacity: 0.18, duration: 0.2 })
        gsap.to(dot, { scale: 0, opacity: 0, duration: 0.12 })
        label.textContent = custom ?? 'Read'
        gsap.to(label, { scale: 1, opacity: 1, duration: 0.28, ease: 'power2.out' })

      } else {
        // default
        gsap.to(ring, { width: 36, height: 36, duration: 0.28, ease: 'power3.out' })
        const poly = ring.querySelector('polygon')
        if (poly) gsap.to(poly, { attr: { strokeWidth: 2 }, strokeOpacity: 0.45, duration: 0.28 })
        const svg = ring.querySelector('svg')
        if (svg) gsap.to(svg, { opacity: 1, duration: 0.2 })
        gsap.to(dot, { scale: 1, opacity: 1, duration: 0.2 })
        gsap.to(label, { scale: 0, opacity: 0, duration: 0.15 })
      }
    }

    /* ── Selectors ── */
    const INTERACTIVE = 'a, button, [role="button"], input, select, textarea, label'
    const TEXT = [
      '.hero-subtitle', '.hero-status', 'p', 'li',
      '.signal-bio', '.bio-line', '.metric-label',
      '[data-cursor-text]',
    ].join(', ')

    /* ── Mouse move ── */
    const onMove = (e: MouseEvent) => {
      targetX = e.clientX
      targetY = e.clientY

      if (!hasEntered) {
        hasEntered = true
        dotX = ringX = e.clientX
        dotY = ringY = e.clientY
        dot.style.transform = `translate(-50%, -50%) translate3d(${e.clientX}px, ${e.clientY}px, 0)`
        ring.style.transform = `translate(-50%, -50%) translate3d(${e.clientX}px, ${e.clientY}px, 0)`
        gsap.to(dot,  { opacity: 1, duration: 0.2 })
        gsap.to(ring, { opacity: 1, duration: 0.2 })
      }

      if (!tracking) {
        tracking = true
        rafId = requestAnimationFrame(tick)
      }
    }

    /* ── Hover detection via mouseover (event delegation, one listener) ── */
    const onOver = (e: MouseEvent) => {
      const t = e.target as Element | null
      if (!t) return
      let next: Mode = 'default'
      if (t.closest(INTERACTIVE)) next = 'interactive'
      else if (t.closest(TEXT)) next = 'text'
      const custom = t.closest('[data-cursor-label]')?.getAttribute('data-cursor-label') || null
      enterMode(next, custom)
    }

    /* ── Click feedback (mousedown = squish+flare, mouseup = restore) ── */
    const onDown = () => {
      gsap.to(ring, { scale: 0.75, duration: 0.12, ease: 'power2.in' })
      gsap.to(dot,  { scale: 1.8, opacity: 1, duration: 0.1, ease: 'power2.out' })
    }
    const onUp = () => {
      const targetScale = mode === 'interactive' ? 0.45 : mode === 'text' ? 0 : 1
      gsap.to(ring, { scale: 1, duration: 0.25, ease: 'power3.out' })
      gsap.to(dot,  { scale: targetScale, duration: 0.2, ease: 'power3.out' })
    }

    /* ── Cursor leaves / enters viewport ── */
    const onDocLeave  = () => {
      gsap.to([dot, ring], { opacity: 0, duration: 0.15 })
      cancelAnimationFrame(rafId)
      tracking = false
    }
    const onDocEnter  = () => {
      if (hasEntered) {
        gsap.to([dot, ring], { opacity: 1, duration: 0.15 })
        if (!tracking) {
          tracking = true
          rafId = requestAnimationFrame(tick)
        }
      }
    }

    window.addEventListener('mousemove',    onMove,      { passive: true })
    document.addEventListener('mouseover',  onOver)
    document.addEventListener('mousedown',  onDown)
    document.addEventListener('mouseup',    onUp)
    document.addEventListener('mouseleave', onDocLeave)
    document.addEventListener('mouseenter', onDocEnter)

    return () => {
      cancelAnimationFrame(rafId)
      window.removeEventListener('mousemove',    onMove)
      document.removeEventListener('mouseover',  onOver)
      document.removeEventListener('mousedown',  onDown)
      document.removeEventListener('mouseup',    onUp)
      document.removeEventListener('mouseleave', onDocLeave)
      document.removeEventListener('mouseenter', onDocEnter)
    }
  }, [isDesktop])

  if (!isDesktop) return null

  return (
    <>
      {/* Inner hex dot */}
      <div
        ref={dotRef}
        className="fixed top-0 left-0 pointer-events-none"
        style={{
          zIndex: 100000,
          width: 10,
          height: 12,
          /* Start off-screen — shown after first mousemove */
          opacity: 0,
          translate: 'none',
          willChange: 'transform',
        }}
        aria-hidden="true"
      >
        <svg viewBox="0 0 100 100" width="100%" height="100%">
          <defs>
            <filter id="cursor-glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="6" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>
          </defs>
          <polygon
            points="50,2 91.6,26 91.6,74 50,98 8.4,74 8.4,26"
            fill="#FF6B00"
            filter="url(#cursor-glow)"
          />
        </svg>
      </div>

      {/* Outer hex ring */}
      <div
        ref={ringRef}
        className="fixed top-0 left-0 pointer-events-none flex items-center justify-center"
        style={{
          zIndex: 100001,
          width: 36,
          height: 36,
          opacity: 0,
          translate: 'none',
          willChange: 'transform',
        }}
        aria-hidden="true"
      >
        <svg viewBox="0 0 100 100" width="100%" height="100%" style={{ overflow: 'visible' }}>
          <defs>
            <filter id="ring-glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="4" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>
          </defs>
          <polygon
            points="50,2 91.6,26 91.6,74 50,98 8.4,74 8.4,26"
            fill="none"
            stroke="#FF6B00"
            strokeWidth="2"
            strokeOpacity="0.45"
            filter="url(#ring-glow)"
          />
        </svg>

        {/* Hover label (Read / Click) */}
        <span
          ref={labelRef}
          className="absolute font-mono uppercase"
          style={{
            fontSize: '8px',
            letterSpacing: '0.2em',
            color: '#FF6B00',
            opacity: 0,
            transform: 'scale(0)',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
          }}
        >
          Read
        </span>
      </div>
    </>
  )
}
